import { PRIMARY_FLOW_PAYLOAD_TIMELINE_TYPE } from 'flare-chat-core/contracts/timeline/item-types';

const array = (value) => (Array.isArray(value) ? value : []);
const text = (value) => (typeof value === 'string' ? value.trim() : '');
const roundKeyFor = (item) => text(item?.roundKey || item?.round_key || item?.payload?.roundKey || item?.payload?.round_key);
const enabledItem = (item) => item?.registered === true && item.enabled === true && item.available === true;

/** Validate a declared projection against the accepted registry, never select a capability from prose. */
const validSkill = (entry, registry) => {
  const skill = array(registry?.skills).find((item) => item.target_type === 'skill' && item.target_id === text(entry?.skill_id));
  const capability = array(registry?.capabilities).find((item) => item.target_type === 'capability'
    && item.target_id === text(entry?.selected_capability_id));
  return Boolean(entry && typeof entry === 'object')
    && ['skill_id', 'selected_capability_id', 'label'].every((key) => Boolean(text(entry[key])))
    && Boolean(text(entry.prompt))
    && entry.command === 'execute_action'
    && !['action_key', 'actionKey', 'action', 'payload'].some((key) => Object.hasOwn(entry, key))
    && array(entry.capability_ids).length > 0 && entry.capability_ids.every((id) => Boolean(text(id)))
    && entry.capability_ids.includes(entry.selected_capability_id)
    && enabledItem(skill) && enabledItem(capability)
    && array(skill.capability_ids).includes(entry.selected_capability_id)
    && array(registry?.available_capability_ids).includes(entry.selected_capability_id);
};

const projectionFor = (item) => {
  const projection = item?.payload?.message_ui?.capability_actions;
  return projection?.source === 'authoritative' && projection.surface === 'completed_message'
    && projection.visible === true ? projection : null;
};
const stripStaleActions = (item) => {
  if (!Object.hasOwn(Object(item), 'messageCapabilityActions')) return item;
  const result = { ...item };
  delete result.messageCapabilityActions;
  return result;
};
const projectSkill = (entry, item, sessionId) => ({
  key: `message-skill:${JSON.stringify([sessionId, item.id, roundKeyFor(item), text(entry.skill_id), text(entry.selected_capability_id)])}`,
  sessionId, messageId: text(item.id), roundKey: roundKeyFor(item),
  skill_id: text(entry.skill_id), target_id: text(entry.skill_id),
  selected_capability_id: text(entry.selected_capability_id), capability_ids: entry.capability_ids.map(text),
  label: text(entry.label), prompt: entry.prompt,
  ...(text(entry.description) ? { description: text(entry.description) } : {}),
  source: 'prompt_template_action', interactionMode: 'composer_prefill', messageActionSurface: 'completed_message',
});

/** @param {Array<object>} timelineItems
 * @param {import('../../application/capabilities/contracts').SkillProjection | null} registry
 * @param {string} sessionId
 * @returns {Array<object & {messageCapabilityActions?: import('../../application/capabilities/contracts').MessageSkillCard[]}>}
 */
export const attachCompletedMessageCapabilityActions = (timelineItems = [], registry = null, sessionId = '') => {
  const items = array(timelineItems).map(stripStaleActions);
  if (!text(sessionId) || !registry) return items;
  const projections = items.reduce((index, item) => {
    const roundKey = roundKeyFor(item);
    if (item?.type === PRIMARY_FLOW_PAYLOAD_TIMELINE_TYPE && roundKey) index.set(roundKey, projectionFor(item));
    return index;
  }, new Map());
  const messages = items.reduce((index, item, position) => {
    const roundKey = roundKeyFor(item);
    if (item?.type === 'message' && item.role === 'assistant' && text(item.id) && roundKey) index.set(roundKey, position);
    return index;
  }, new Map());
  const actionsByIndex = new Map([...messages].map(([roundKey, position]) => {
    const actions = array(projections.get(roundKey)?.actions).filter((entry) => validSkill(entry, registry))
      .map((entry) => projectSkill(entry, items[position], sessionId));
    return [position, [...new Map(actions.map((entry) => [entry.key, entry])).values()]];
  }));
  return items.map((item, index) => {
    const actions = actionsByIndex.get(index);
    return actions?.length ? { ...item, messageCapabilityActions: actions } : item;
  });
};
